import { useQuery } from "@apollo/client/react";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { CARD_STATE_COLORS, CHART_UI_COLORS, DATA_VIZ_COLORS } from "../../../config/chartColors";
import { CardStateBreakdownDocument } from "../../../generated/graphql";

interface CardStateBreakdownChartProps {
  lessonId: string;
  studentId?: string;
}

const STATE_LABELS: Record<string, string> = {
  NEW: "New",
  LEARNING: "Learning",
  REVIEW: "Review",
  RELEARNING: "Relearning",
};

// Map FSRS states to chart colors
const STATE_COLORS: Record<string, string> = {
  NEW: CARD_STATE_COLORS.new,
  LEARNING: CARD_STATE_COLORS.learning,
  REVIEW: CARD_STATE_COLORS.review,
  RELEARNING: CARD_STATE_COLORS.relearning,
};

export function CardStateBreakdownChart({ lessonId, studentId }: CardStateBreakdownChartProps) {
  const {
    data,
    loading: isLoading,
    error,
  } = useQuery(CardStateBreakdownDocument, {
    variables: {
      lessonId,
      studentId,
    },
  });

  if (isLoading) {
    return (
      <div className="chart-container">
        <div className="chart-loading">Loading chart...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-container">
        <div className="chart-error">Error loading chart: {error.message}</div>
      </div>
    );
  }

  if (!data?.cardStateBreakdown || data.cardStateBreakdown.length === 0) {
    return (
      <div className="chart-container">
        <div className="chart-empty">No data available</div>
      </div>
    );
  }

  const chartData = data.cardStateBreakdown
    .filter((item) => item.count > 0 && item.state !== "NEW")
    .map((item) => ({
      name: STATE_LABELS[item.state] ?? item.state,
      value: item.count,
      percentage: item.percentage,
    }));

  if (chartData.length === 0) {
    return (
      <div className="chart-container">
        <div className="chart-empty">No cards in this lesson yet</div>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h4 className="h6">Card State Breakdown</h4>
      </div>
      <div className="chart-body">
        <ResponsiveContainer width="100%" height={320}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={100}
              label={(entry) => `${entry.name}: ${((entry.percent ?? 0) * 100).toFixed(1)}%`}
            >
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={STATE_COLORS[entry.name] || DATA_VIZ_COLORS.primary} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: CHART_UI_COLORS.tooltip.background,
                border: `1px solid ${CHART_UI_COLORS.tooltip.border}`,
                borderRadius: "0.5rem",
              }}
              labelStyle={{ color: CHART_UI_COLORS.tooltip.label }}
              itemStyle={{ color: CHART_UI_COLORS.tooltip.text }}
              formatter={(value, name, props) => [
                `${value} cards (${props.payload.percentage}%)`,
                name,
              ]}
            />
            <Legend wrapperStyle={{ color: CHART_UI_COLORS.legend }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
